import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SuspenseAccountBalance {
  accountId: string | null;
  balance: number;
  transactionCount: number;
  isLoading: boolean;
  hasBalance: boolean;
}

/**
 * Read-only hook to check suspense account balance and transaction count.
 * Unlike useSuspenseAccount, this does NOT create the account as a side effect.
 * Uses a single RPC call that computes balance at the DB level.
 */
export function useSuspenseAccountBalance(organizationId: string | undefined): SuspenseAccountBalance {
  const { data, isLoading } = useQuery({
    queryKey: ['suspense-account-balance', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_suspense_account_balance', {
        p_organization_id: organizationId!,
      });

      if (error) throw error;

      // RPC returns empty array if no suspense account exists
      if (!data || data.length === 0) {
        return { accountId: null, balance: 0, transactionCount: 0 };
      }

      const row = data[0];
      return {
        accountId: row.account_id as string,
        balance: Number(row.balance),
        transactionCount: Number(row.transaction_count),
      };
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
  });

  const accountId = data?.accountId ?? null;
  const balance = data?.balance ?? 0;
  const transactionCount = data?.transactionCount ?? 0;

  return {
    accountId,
    balance,
    transactionCount,
    isLoading,
    hasBalance: Math.abs(balance) > 0.01,
  };
}
